import { useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import {
  useCreateMaquilaItem,
  useUpdateMaquilaItem,
} from "~/hooks/maquila";
import type { MaquilaItem, MaquilaItemFormData } from "~/types/maquila";
import { AddMaquilaClientDialog } from "./AddMaquilaClientDialog";

interface MaquilaItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item?: MaquilaItem | null;
  clients: Array<{ id: string; name: string }>;
  onRefetchClients?: () => void;
}

const UNITS = [
  { value: "kg", label: "Kilogramos (kg)" },
  { value: "lt", label: "Litros (lt)" },
  { value: "unidades", label: "Unidades" },
  { value: "cajas", label: "Cajas" },
  { value: "bolsas", label: "Bolsas" },
  { value: "tambores", label: "Tambores" },
  { value: "pallets", label: "Pallets" },
];

export const MaquilaItemDialog = ({
  open,
  onOpenChange,
  item,
  clients,
  onRefetchClients,
}: MaquilaItemDialogProps) => {
  const [clientId, setClientId] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [unit, setUnit] = useState("kg");
  const [description, setDescription] = useState("");
  const [addClientOpen, setAddClientOpen] = useState(false);

  const createMutation = useCreateMaquilaItem();
  const updateMutation = useUpdateMaquilaItem();

  const isEditing = !!item;
  const isPending = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (item) {
      setClientId(item.client_id);
      setName(item.name);
      setCode(item.code || "");
      setUnit(item.unit);
      setDescription(item.description || "");
    } else {
      setClientId("");
      setName("");
      setCode("");
      setUnit("kg");
      setDescription("");
    }
  }, [item, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const formData: MaquilaItemFormData = {
      client_id: clientId,
      name,
      code: code || undefined,
      unit,
      description: description || undefined,
    };

    try {
      if (isEditing && item) {
        await updateMutation.mutateAsync({ id: item.id, data: formData });
      } else {
        await createMutation.mutateAsync(formData);
      }
      onOpenChange(false);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const isValid = clientId && name && unit;

  const handleClientAdded = (client: { id: string; name: string }) => {
    setClientId(client.id);
    onRefetchClients?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Material" : "Nuevo Material"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Cliente *</Label>
              {!isEditing && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => setAddClientOpen(true)}
                >
                  <Plus className="mr-1 h-3 w-3" />
                  Nuevo
                </Button>
              )}
            </div>
            <Select
              value={clientId}
              onValueChange={setClientId}
              disabled={isEditing}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar cliente" />
              </SelectTrigger>
              <SelectContent>
                {clients.length === 0 ? (
                  <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                    No hay clientes maquila.
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="block mx-auto mt-1"
                      onClick={() => setAddClientOpen(true)}
                    >
                      Crear primer cliente
                    </Button>
                  </div>
                ) : (
                  clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Nombre del Material *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Aceite de Oliva, Envases 500ml"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Codigo (opcional)</Label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="SKU o codigo interno"
              />
            </div>
            <div className="space-y-2">
              <Label>Unidad *</Label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UNITS.map((u) => (
                    <SelectItem key={u.value} value={u.value}>
                      {u.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Descripcion (opcional)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detalles adicionales..."
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={!isValid || isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? "Guardar Cambios" : "Crear Material"}
            </Button>
          </DialogFooter>
        </form>

        <AddMaquilaClientDialog
          open={addClientOpen}
          onOpenChange={setAddClientOpen}
          onClientAdded={handleClientAdded}
        />
      </DialogContent>
    </Dialog>
  );
};
